import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import AuthService from "../service/AmplifyAuth";
import TrustdService from "../service/TrustdService";
import { Button, Field, Modal } from "../components/UI";
import {
  apiHeaders,
  formatDate,
  getCachedStripeUser,
  getStripeUserByEmail,
  invalidateStripeUserCache,
  useToast,
} from "../hooks";
import { useNavigate } from "../router";

const STATE_ACCOUNT = "STATE_ACCOUNT";
const STATE_ACTIVE_ACCOUNT = "STATE_ACTIVE_ACCOUNT";
const STATE_HOSTAWAY_ACCOUNT = "STATE_HOSTAWAY_ACCOUNT";
const TOGGLE_ID_VERIFICATION = "idVerification";
const TOGGLE_SELFIE_VERIFICATION = "selfieVerification";
const TOGGLE_SEND_EMAIL = "sendEmail";
const EMPTY_MANUAL_LISTING = { name: "", address: "" };

function AccountValue({ label, value }) {
  const displayValue =
    value === null || value === undefined || value === "" ? "N/A" : value;
  return (
    <div className="account-value">
      <span>{label}</span>
      <strong>{displayValue}</strong>
    </div>
  );
}

function AccountSection({ title, actions, children }) {
  return (
    <section className="account-section">
      <div className="account-section-header">
        <h3>{title}</h3>
        {actions ? (
          <div className="account-section-actions">{actions}</div>
        ) : null}
      </div>
      <div className="account-section-body">{children}</div>
    </section>
  );
}

export default function UserAccount() {
  const cachedUser = AuthService.getCachedUserAttributes?.() || null;
  const initialDetailedUser = cachedUser?.username
    ? getCachedStripeUser(cachedUser.username)
    : null;
  const [user, setUser] = useState(cachedUser);
  const [detailedUser, setDetailedUser] = useState(initialDetailedUser);
  const [memberSince, setMemberSince] = useState(
    initialDetailedUser?.createdDate?.substring(0, 10) || "",
  );
  const [currentStage, setCurrentStage] = useState(STATE_ACCOUNT);
  const [userLinkedIsLoading, setUserLinkedIsLoading] = useState(true);
  const [userLinked, setUserLinked] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [accountId, setAccountId] = useState("");
  const [sendEmail, setSendEmail] = useState(true);
  const [isIdVerificationEnabled, setIsIdVerificationEnabled] = useState(false);
  const [isSelfieVerificationEnabled, setIsSelfieVerificationEnabled] = useState(false);
  const [listings, setListings] = useState([]);
  const [showManualListing, setShowManualListing] = useState(false);
  const [manualListing, setManualListing] = useState(EMPTY_MANUAL_LISTING);
  const [manualListingSaving, setManualListingSaving] = useState(false);
  const [manualListingError, setManualListingError] = useState("");
  const [listingSettingsRefreshing, setListingSettingsRefreshing] = useState(false);
  const [listingSettingsError, setListingSettingsError] = useState(false);
  const [listingSavingIds, setListingSavingIds] = useState({});
  const [bulkListingSettingsSaving, setBulkListingSettingsSaving] = useState(false);
  const [toggleSavingState, setToggleSavingState] = useState({
    [TOGGLE_ID_VERIFICATION]: false,
    [TOGGLE_SELFIE_VERIFICATION]: false,
    [TOGGLE_SEND_EMAIL]: false,
  });
  const [isPopupClosed, setIsPopupClosed] = useState(true);
  const [popupStep, setPopupStep] = useState(1);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnectingPms, setIsDisconnectingPms] = useState(false);
  const confirmedToggleRef = useRef({
    [TOGGLE_ID_VERIFICATION]: false,
    [TOGGLE_SELFIE_VERIFICATION]: false,
    [TOGGLE_SEND_EMAIL]: true,
  });
  const desiredToggleRef = useRef({
    [TOGGLE_ID_VERIFICATION]: false,
    [TOGGLE_SELFIE_VERIFICATION]: false,
    [TOGGLE_SEND_EMAIL]: true,
  });
  const inFlightToggleRef = useRef({
    [TOGGLE_ID_VERIFICATION]: false,
    [TOGGLE_SELFIE_VERIFICATION]: false,
    [TOGGLE_SEND_EMAIL]: false,
  });
  const addToast = useToast();
  const navigate = useNavigate();
  const resolveHostEmail = (details = detailedUser) => {
    const fallback =
      details?.email || details?.username || user?.username || "";
    return String(fallback || "")
      .trim()
      .toLowerCase();
  };
  const formatChannelLabel = (channel = "") => {
    const normalized = String(channel || "").trim();
    return normalized
      ? `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`
      : "";
  };
  const loadAccountListings = useCallback(async (hostEmail) => {
    if (!hostEmail) return;
    const cached = TrustdService.getCachedListings(hostEmail);
    if (cached?.listings) setListings(cached.listings.filter((item) => item.isActive));
    setListingSettingsRefreshing(true);
    try {
      const local = await TrustdService.getListings(hostEmail, { sync: false });
      setListings((local.listings || []).filter((item) => item.isActive));
      setListingSettingsError(false);
      await TrustdService.syncListings(hostEmail);
      const fresh = await TrustdService.getListings(hostEmail, { sync: false });
      setListings((fresh.listings || []).filter((item) => item.isActive));
    } catch {
      setListingSettingsError(true);
    } finally {
      setListingSettingsRefreshing(false);
    }
  }, []);
  const applyIdVerificationValue = useCallback((nextValue) => {
    const normalized = Boolean(nextValue);
    setIsIdVerificationEnabled(normalized);
    setDetailedUser((value) =>
      value ? { ...value, isIdVerificationEnabled: normalized } : value,
    );
  }, []);
  const applySelfieVerificationValue = useCallback((nextValue) => {
    const normalized = Boolean(nextValue);
    setIsSelfieVerificationEnabled(normalized);
    setDetailedUser((value) =>
      value ? { ...value, isSelfieVerificationEnabled: normalized } : value,
    );
  }, []);
  const applySendEmailValue = useCallback((nextValue) => {
    const normalized = Boolean(nextValue);
    setSendEmail(normalized);
    setDetailedUser((value) =>
      value ? { ...value, sendEmail: normalized ? "YES" : "NO" } : value,
    );
  }, []);
  const syncToggleTracking = useCallback(
    (nextIdVerificationValue, nextSelfieVerificationValue, nextSendEmailValue) => {
      const normalizedIdVerification = Boolean(nextIdVerificationValue);
      const normalizedSelfieVerification = Boolean(nextSelfieVerificationValue);
      const normalizedSendEmail = Boolean(nextSendEmailValue);
      confirmedToggleRef.current[TOGGLE_ID_VERIFICATION] =
        normalizedIdVerification;
      confirmedToggleRef.current[TOGGLE_SEND_EMAIL] = normalizedSendEmail;
      confirmedToggleRef.current[TOGGLE_SELFIE_VERIFICATION] = normalizedSelfieVerification;
      desiredToggleRef.current[TOGGLE_ID_VERIFICATION] =
        normalizedIdVerification;
      desiredToggleRef.current[TOGGLE_SEND_EMAIL] = normalizedSendEmail;
      desiredToggleRef.current[TOGGLE_SELFIE_VERIFICATION] = normalizedSelfieVerification;
    },
    [],
  );
  const setToggleSaving = useCallback((toggleKey, nextValue) => {
    setToggleSavingState((current) => {
      if (current[toggleKey] === nextValue) return current;
      return { ...current, [toggleKey]: nextValue };
    });
  }, []);
  const queueToggleSync = useCallback(
    (toggleKey, nextValue, persist) => {
      desiredToggleRef.current[toggleKey] = nextValue;
      if (toggleKey === TOGGLE_ID_VERIFICATION)
        applyIdVerificationValue(nextValue);
      if (toggleKey === TOGGLE_SELFIE_VERIFICATION)
        applySelfieVerificationValue(nextValue);
      if (toggleKey === TOGGLE_SEND_EMAIL) applySendEmailValue(nextValue);
      if (inFlightToggleRef.current[toggleKey]) return;

      void (async () => {
        inFlightToggleRef.current[toggleKey] = true;
        setToggleSaving(toggleKey, true);
        try {
          while (
            desiredToggleRef.current[toggleKey] !==
            confirmedToggleRef.current[toggleKey]
          ) {
            const requestedValue = desiredToggleRef.current[toggleKey];
            const persistedValue = await persist(requestedValue);
            const normalizedPersistedValue =
              typeof persistedValue === "boolean"
                ? persistedValue
                : requestedValue;
            confirmedToggleRef.current[toggleKey] = normalizedPersistedValue;
            if (
              normalizedPersistedValue !== desiredToggleRef.current[toggleKey]
            ) {
              desiredToggleRef.current[toggleKey] = normalizedPersistedValue;
              if (toggleKey === TOGGLE_ID_VERIFICATION)
                applyIdVerificationValue(normalizedPersistedValue);
              if (toggleKey === TOGGLE_SELFIE_VERIFICATION)
                applySelfieVerificationValue(normalizedPersistedValue);
              if (toggleKey === TOGGLE_SEND_EMAIL)
                applySendEmailValue(normalizedPersistedValue);
            }
          }
        } catch (error) {
          const rollbackValue = confirmedToggleRef.current[toggleKey];
          desiredToggleRef.current[toggleKey] = rollbackValue;
          if (toggleKey === TOGGLE_ID_VERIFICATION)
            applyIdVerificationValue(rollbackValue);
          if (toggleKey === TOGGLE_SELFIE_VERIFICATION)
            applySelfieVerificationValue(rollbackValue);
          if (toggleKey === TOGGLE_SEND_EMAIL)
            applySendEmailValue(rollbackValue);
          addToast({
            severity: "error",
            summary: "Update Failed",
            detail: "Setting could not be saved. Please try again.",
            life: 7000,
          });
        } finally {
          inFlightToggleRef.current[toggleKey] = false;
          setToggleSaving(toggleKey, false);
        }
      })();
    },
    [addToast, applyIdVerificationValue, applySelfieVerificationValue, applySendEmailValue, setToggleSaving],
  );

  const consumePmsOAuthResult = () => {
    const params = new URLSearchParams(window.location.search);
    const status = String(params.get("pms_oauth") || "")
      .trim()
      .toLowerCase();
    if (!status) return;

    const provider = String(params.get("provider") || "pms").trim();
    const providerLabel = provider
      ? provider[0].toUpperCase() + provider.slice(1)
      : "PMS";
    const reason = String(params.get("reason") || "").trim();

    if (status === "success") {
      addToast({
        severity: "success",
        summary: "Connected",
        detail: `${providerLabel} account connected successfully.`,
        life: 6000,
      });
    } else {
      addToast({
        severity: "error",
        summary: "Connection Failed",
        detail:
          reason || `${providerLabel} authorization could not be completed.`,
        life: 10000,
      });
    }

    params.delete("pms_oauth");
    params.delete("provider");
    params.delete("reason");
    params.delete("externalAccountId");
    const query = params.toString();
    const nextUrl = query
      ? `${window.location.pathname}?${query}${window.location.hash}`
      : `${window.location.pathname}${window.location.hash}`;
    window.history.replaceState({}, document.title, nextUrl);
  };

  useEffect(() => {
    async function load() {
      consumePmsOAuthResult();
      const authenticated =
        await AuthService.currentAuthenticatedUserAttributes();
      setUser(authenticated);
      void loadAccountListings(String(authenticated.username || "").toLowerCase());
      const cachedDetailed = getCachedStripeUser(authenticated.username);
      if (cachedDetailed) {
        const cachedSendEmail = cachedDetailed.sendEmail === "YES";
        const cachedIdVerification = Boolean(
          cachedDetailed.isIdVerificationEnabled,
        );
        const cachedSelfieVerification = Boolean(
          cachedDetailed.isSelfieVerificationEnabled,
        );
        setMemberSince(cachedDetailed?.createdDate?.substring(0, 10) || "...");
        setDetailedUser(cachedDetailed);
        setSendEmail(cachedSendEmail);
        setIsIdVerificationEnabled(cachedIdVerification);
        setIsSelfieVerificationEnabled(cachedSelfieVerification);
        syncToggleTracking(cachedIdVerification, cachedSelfieVerification, cachedSendEmail);
        setIsPopupClosed(Boolean(cachedDetailed.isPopupClosed));
      }

      const freshDetailed = await getStripeUserByEmail(authenticated.username, {
        forceRefresh: true,
      });
      const resolvedDetailed = freshDetailed || cachedDetailed || null;
      if (!resolvedDetailed) return;

      const resolvedSendEmail = resolvedDetailed.sendEmail === "YES";
      const resolvedIdVerification = Boolean(
        resolvedDetailed.isIdVerificationEnabled,
      );
      const resolvedSelfieVerification = Boolean(
        resolvedDetailed.isSelfieVerificationEnabled,
      );
      setMemberSince(resolvedDetailed?.createdDate?.substring(0, 10) || "...");
      setDetailedUser(resolvedDetailed);
      setSendEmail(resolvedSendEmail);
      setIsIdVerificationEnabled(resolvedIdVerification);
      setIsSelfieVerificationEnabled(resolvedSelfieVerification);
      syncToggleTracking(resolvedIdVerification, resolvedSelfieVerification, resolvedSendEmail);
      setIsPopupClosed(Boolean(resolvedDetailed.isPopupClosed));
      window.setTimeout(() => controlLink(resolvedDetailed), 3000);
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadAccountListings, syncToggleTracking]);

  const controlLink = async (details) => {
    try {
      const email = resolveHostEmail(details);
      if (!email) {
        setUserLinked(false);
        return;
      }
      let response = null;
      if (details.channel === "ownerrez")
        response = await axios.get(
          `/v1/integrations/tpc/ownerrez/user/${email}`,
          { headers: apiHeaders, timeout: 0 },
        );
      if (details.channel === "hostaway")
        response = await axios.get(
          `/v1/integrations/tpc/hostaway/user/${email}`,
          { headers: apiHeaders, timeout: 0 },
        );
      if (details.channel === "tokeet" || details.channel === "advancecm")
        response = await axios.get(
          `/v1/integrations/stripe/pms/tokeetAuthCheck/${email}`,
          { headers: apiHeaders, timeout: 0 },
        );
      if (details.channel === "hospitable")
        response = await axios.get(
          `/v1/integrations/stripe/pms/hospitableAuthCheck/${email}`,
          { headers: apiHeaders, timeout: 0 },
        );
      if (
        response &&
        (response.data === "" || response.data.status === "Error")
      )
        setUserLinked(false);
    } catch (error) {
      setUserLinked(false);
    } finally {
      setUserLinkedIsLoading(false);
    }
  };

  const reload = async () => {
    const authenticated =
      await AuthService.currentAuthenticatedUserAttributes();
    const res = await getStripeUserByEmail(authenticated.username, {
      forceRefresh: true,
    });
    setUser(authenticated);
    setDetailedUser(res);
    if (res) {
      const nextSendEmail = res.sendEmail === "YES";
      const nextIdVerification = Boolean(res.isIdVerificationEnabled);
      const nextSelfieVerification = Boolean(res.isSelfieVerificationEnabled);
      setSendEmail(nextSendEmail);
      setIsIdVerificationEnabled(nextIdVerification);
      setIsSelfieVerificationEnabled(nextSelfieVerification);
      syncToggleTracking(nextIdVerification, nextSelfieVerification, nextSendEmail);
    }
  };

  const linkAccount = async () => {
    const hostEmail = resolveHostEmail();
    if (!hostEmail) return;
    if (detailedUser.channel === "ownerrez") {
      const res = await TrustdService.ownerrezTempUserCreate({
        email: hostEmail,
        name: detailedUser.contactName,
        company: detailedUser.company,
        phone: detailedUser.phone,
        terms: "on",
      });
      if (res.data.status === "success") {
        addToast({
          severity: "success",
          summary: "Redirecting...",
          detail: res.data.message,
          life: 5000,
        });
        window.setTimeout(() => {
          window.location.href = res.data.redirect;
        }, 3000);
      }
    } else if (detailedUser.channel === "hostaway") {
      setCurrentStage(STATE_HOSTAWAY_ACCOUNT);
    } else if (detailedUser.channel === "hospitable") {
      const res = await TrustdService.redirectHospitable(hostEmail);
      if (res.data.status === "success")
        window.setTimeout(() => {
          window.location.href = res.data.redirect;
        }, 3000);
    } else {
      const res = await TrustdService.redirectTokeet(
        hostEmail,
        detailedUser.channel,
      );
      if (res.data.status === "success")
        window.setTimeout(() => {
          window.location.href = res.data.redirect;
        }, 3000);
    }
  };

  const syncAccountListings = async () => {
    if (!userLinked) {
      await linkAccount();
      return;
    }
    const hostEmail = resolveHostEmail();
    if (!hostEmail || listingSettingsRefreshing) return;
    setListingSettingsRefreshing(true);
    try {
      const result = await TrustdService.syncListings(hostEmail);
      const fresh = await TrustdService.getListings(hostEmail, { sync: false });
      setListings((fresh.listings || []).filter((item) => item.isActive));
      setListingSettingsError(false);
      addToast({
        severity: result?.warnings?.length ? "warn" : "success",
        summary: "PMS Listings",
        detail: result?.warnings?.length
          ? "Listings were updated, but one or more PMS connections could not be refreshed."
          : "Your PMS listings are up to date.",
        life: 5000,
      });
    } catch (error) {
      setListingSettingsError(true);
      addToast({
        severity: "error",
        summary: "PMS Listings",
        detail: error?.response?.data?.message || "PMS listings could not be synced.",
        life: 6000,
      });
    } finally {
      setListingSettingsRefreshing(false);
    }
  };

  const saveManualListing = async (event) => {
    event.preventDefault();
    const name = manualListing.name.trim();
    if (!name) {
      setManualListingError("Listing name is required.");
      return;
    }
    const hostEmail = resolveHostEmail();
    if (!hostEmail) return;
    setManualListingSaving(true);
    setManualListingError("");
    try {
      const saved = await TrustdService.createListing(hostEmail, {
        name,
        address: manualListing.address.trim() || null,
        isScreeningEnabled: true,
        isIdVerificationEnabled: isIdVerificationEnabled || isSelfieVerificationEnabled,
        isSelfieVerificationEnabled,
      });
      setListings((items) => [...items, saved].sort((left, right) => left.name.localeCompare(right.name)));
      setManualListing(EMPTY_MANUAL_LISTING);
      setShowManualListing(false);
      addToast({ severity: "success", summary: "Listing Added", detail: `${saved.name} was added successfully.`, life: 3500 });
    } catch (error) {
      setManualListingError(error?.response?.data?.message || "The listing could not be added.");
    } finally {
      setManualListingSaving(false);
    }
  };

  const deleteManualListing = async (listing) => {
    if (listing.source !== "manual" || listingSavingIds[listing.id]) return;
    const hostEmail = resolveHostEmail();
    if (!hostEmail) return;
    setListingSavingIds((value) => ({ ...value, [listing.id]: true }));
    try {
      await TrustdService.deleteListing(hostEmail, listing.id);
      setListings((items) => items.filter((item) => item.id !== listing.id));
      addToast({
        severity: "success",
        summary: "Listing Deleted",
        detail: `${listing.name} was deleted successfully.`,
        life: 3500,
      });
    } catch (error) {
      addToast({
        severity: "error",
        summary: "Delete Listing",
        detail:
          error?.response?.data?.message ||
          "The listing could not be deleted. Please try again.",
        life: 6000,
      });
    } finally {
      setListingSavingIds((value) => ({ ...value, [listing.id]: false }));
    }
  };

  const delinkAccount = async () => {
    const hostEmail = resolveHostEmail();
    if (!hostEmail) return;
    setIsDisconnectingPms(true);
    try {
      await axios.delete(`/v1/integrations/stripe/users/pms/${hostEmail}`, {
        headers: apiHeaders,
      });
      setUserLinked(false);
      window.dispatchEvent(new Event("trustd:pms-link-updated"));
      setShowDisconnectConfirm(false);
      addToast({
        severity: "success",
        summary: "Disconnected",
        detail: "Your PMS account has been disconnected successfully.",
        life: 6000,
      });
    } catch (error) {
      addToast({
        severity: "error",
        summary: "Disconnect Failed",
        detail:
          error?.response?.data?.message ||
          "Your PMS account could not be disconnected. Please try again.",
        life: 8000,
      });
    } finally {
      setIsDisconnectingPms(false);
    }
  };

  const completeHostaway = async () => {
    const hostEmail = resolveHostEmail();
    if (!hostEmail) return;
    if (!String(accountId || "").trim() || !String(apiKey || "").trim()) {
      addToast({
        severity: "error",
        summary: "Validation Error",
        detail: "Account ID and API Key are required.",
        life: 7000,
      });
      return;
    }

    try {
      await TrustdService.saveUserInfo({
        username: hostEmail,
        contactName: detailedUser.contactName,
        company: detailedUser.company,
        email: hostEmail,
        website: null,
        phoneNumber: detailedUser.phoneNumber,
        address: null,
        sendEmail: detailedUser.sendEmail,
        channel: detailedUser.channel,
        accountId,
        apiKey,
        screening: detailedUser.screening,
        freeScreening: detailedUser.freeScreening,
        status: "ACTIVE",
      });
      addToast({
        severity: "success",
        summary: "Connection Completed",
        detail: "Redirecting to account page...",
        life: 5000,
      });
      window.dispatchEvent(new Event("trustd:pms-link-updated"));
      window.setTimeout(() => (window.location.href = "/account"), 3000);
    } catch (error) {
      setUserLinked(false);
      addToast({
        severity: "error",
        summary: "Connection Failed",
        detail: String(
          error?.message || "Hostaway connection could not be completed.",
        ),
        life: 8000,
      });
    }
  };

  const applyDefaultSettingsToLoadedListings = (idEnabled, selfieEnabled) => {
    const hostEmail = resolveHostEmail();
    if (hostEmail) TrustdService.applyListingsDefaultsToCache(hostEmail, {
      isIdVerificationEnabled: idEnabled,
      isSelfieVerificationEnabled: selfieEnabled,
    });
    setListings((items) => items.map((listing) => ({
      ...listing,
      isScreeningEnabled: true,
      isIdVerificationEnabled: Boolean(idEnabled || selfieEnabled),
      isSelfieVerificationEnabled: Boolean(selfieEnabled),
    })));
  };

  const changeIdVerification = async (applyToAllListings = false) => {
    const hostEmail = resolveHostEmail();
    if (!hostEmail) return;
    const shouldApplyToAll = applyToAllListings === true;
    const nextValue = !desiredToggleRef.current[TOGGLE_ID_VERIFICATION];
    queueToggleSync(
      TOGGLE_ID_VERIFICATION,
      nextValue,
      async (requestedValue) => {
        if (shouldApplyToAll) {
          const selfieValue = requestedValue
            ? desiredToggleRef.current[TOGGLE_SELFIE_VERIFICATION]
            : false;
          const responseData = await TrustdService.applyVerificationDefaults(hostEmail, {
            isIdVerificationEnabled: requestedValue,
            isSelfieVerificationEnabled: selfieValue,
          });
          invalidateStripeUserCache(hostEmail);
          const resolvedId = Boolean(responseData.isIdVerificationEnabled);
          const resolvedSelfie = Boolean(responseData.isSelfieVerificationEnabled);
          applySelfieVerificationValue(resolvedSelfie);
          confirmedToggleRef.current[TOGGLE_SELFIE_VERIFICATION] = resolvedSelfie;
          desiredToggleRef.current[TOGGLE_SELFIE_VERIFICATION] = resolvedSelfie;
          applyDefaultSettingsToLoadedListings(resolvedId, resolvedSelfie);
          return resolvedId;
        }
        const endpoint = requestedValue
          ? "turn-on-id-verification"
          : "change-id-verification";
        const response = await axios.get(`/v1/users/${endpoint}/${hostEmail}`, {
          headers: apiHeaders,
        });
        invalidateStripeUserCache(hostEmail);
        if (typeof response?.data?.isSelfieVerificationEnabled === "boolean") {
          const selfieValue = Boolean(response.data.isSelfieVerificationEnabled);
          applySelfieVerificationValue(selfieValue);
          confirmedToggleRef.current[TOGGLE_SELFIE_VERIFICATION] = selfieValue;
          desiredToggleRef.current[TOGGLE_SELFIE_VERIFICATION] = selfieValue;
        }
        if (typeof response?.data?.isIdVerificationEnabled === "boolean") {
          return Boolean(response.data.isIdVerificationEnabled);
        }
        return requestedValue;
      },
    );
  };

  const changeSelfieVerification = async (applyToAllListings = false) => {
    const hostEmail = resolveHostEmail();
    if (!hostEmail) return;
    const shouldApplyToAll = applyToAllListings === true;
    const nextValue = !desiredToggleRef.current[TOGGLE_SELFIE_VERIFICATION];
    if (nextValue && !desiredToggleRef.current[TOGGLE_ID_VERIFICATION]) {
      addToast({
        severity: "info",
        summary: "ID Verification Required",
        detail: "To verify a selfie, ID Verification must also be enabled. We'll enable it automatically.",
        life: 9000,
      });
    }
    queueToggleSync(
      TOGGLE_SELFIE_VERIFICATION,
      nextValue,
      async (requestedValue) => {
        if (shouldApplyToAll) {
          const idValue = requestedValue
            ? true
            : desiredToggleRef.current[TOGGLE_ID_VERIFICATION];
          const responseData = await TrustdService.applyVerificationDefaults(hostEmail, {
            isIdVerificationEnabled: idValue,
            isSelfieVerificationEnabled: requestedValue,
          });
          invalidateStripeUserCache(hostEmail);
          const resolvedId = Boolean(responseData.isIdVerificationEnabled);
          const resolvedSelfie = Boolean(responseData.isSelfieVerificationEnabled);
          applyIdVerificationValue(resolvedId);
          confirmedToggleRef.current[TOGGLE_ID_VERIFICATION] = resolvedId;
          desiredToggleRef.current[TOGGLE_ID_VERIFICATION] = resolvedId;
          applyDefaultSettingsToLoadedListings(resolvedId, resolvedSelfie);
          return resolvedSelfie;
        }
        const endpoint = requestedValue
          ? "turn-on-selfie-verification"
          : "change-selfie-verification";
        const response = await axios.get(`/v1/users/${endpoint}/${hostEmail}`, {
          headers: apiHeaders,
        });
        invalidateStripeUserCache(hostEmail);
        if (typeof response?.data?.isIdVerificationEnabled === "boolean") {
          const idValue = Boolean(response.data.isIdVerificationEnabled);
          applyIdVerificationValue(idValue);
          confirmedToggleRef.current[TOGGLE_ID_VERIFICATION] = idValue;
          desiredToggleRef.current[TOGGLE_ID_VERIFICATION] = idValue;
        }
        if (typeof response?.data?.isSelfieVerificationEnabled === "boolean") {
          return Boolean(response.data.isSelfieVerificationEnabled);
        }
        return requestedValue;
      },
    );
  };

  const changeListingRequirement = async (listing, key) => {
    if (listingSavingIds[listing.id] || key === "isScreeningEnabled") return;
    const next = {
      isScreeningEnabled: listing.isScreeningEnabled,
      isIdVerificationEnabled: listing.isIdVerificationEnabled,
      isSelfieVerificationEnabled: listing.isSelfieVerificationEnabled,
      [key]: !listing[key],
    };
    if (key === "isSelfieVerificationEnabled" && next.isSelfieVerificationEnabled) {
      next.isIdVerificationEnabled = true;
    }
    if (key === "isIdVerificationEnabled" && !next.isIdVerificationEnabled) {
      next.isSelfieVerificationEnabled = false;
    }
    setListings((items) => items.map((item) => item.id === listing.id ? { ...item, ...next } : item));
    setListingSavingIds((value) => ({ ...value, [listing.id]: true }));
    try {
      const saved = await TrustdService.updateListing(resolveHostEmail(), listing.id, next);
      setListings((items) => items.map((item) => item.id === listing.id ? saved : item));
    } catch (error) {
      setListings((items) => items.map((item) => item.id === listing.id ? listing : item));
      addToast({ severity: "error", summary: "Listing Settings", detail: "The listing override could not be saved. Default settings will be used if the override is unavailable.", life: 6000 });
    } finally {
      setListingSavingIds((value) => ({ ...value, [listing.id]: false }));
    }
  };

  const applySettingsToAllListings = async () => {
    const hostEmail = resolveHostEmail();
    if (!hostEmail || bulkListingSettingsSaving) return;
    setBulkListingSettingsSaving(true);
    try {
      const response = await TrustdService.applyVerificationDefaults(hostEmail, {
        isIdVerificationEnabled:
          desiredToggleRef.current[TOGGLE_ID_VERIFICATION],
        isSelfieVerificationEnabled:
          desiredToggleRef.current[TOGGLE_SELFIE_VERIFICATION],
      });
      const idValue = Boolean(response.isIdVerificationEnabled || response.isSelfieVerificationEnabled);
      const selfieValue = Boolean(response.isSelfieVerificationEnabled);
      applyIdVerificationValue(idValue);
      applySelfieVerificationValue(selfieValue);
      confirmedToggleRef.current[TOGGLE_ID_VERIFICATION] = idValue;
      desiredToggleRef.current[TOGGLE_ID_VERIFICATION] = idValue;
      confirmedToggleRef.current[TOGGLE_SELFIE_VERIFICATION] = selfieValue;
      desiredToggleRef.current[TOGGLE_SELFIE_VERIFICATION] = selfieValue;
      applyDefaultSettingsToLoadedListings(idValue, selfieValue);
      invalidateStripeUserCache(hostEmail);
      addToast({ severity: "success", summary: "All Listings Updated", detail: "The current default settings are applied to all listings.", life: 3200 });
    } catch (error) {
      addToast({ severity: "error", summary: "All Listings", detail: error?.response?.data?.message || "The settings could not be applied to all listings.", life: 6000 });
    } finally { setBulkListingSettingsSaving(false); }
  };

  const changeSendEmail = async () => {
    const hostEmail = resolveHostEmail();
    if (!hostEmail) return;
    const nextValue = !desiredToggleRef.current[TOGGLE_SEND_EMAIL];
    queueToggleSync(TOGGLE_SEND_EMAIL, nextValue, async (requestedValue) => {
      await axios.post(
        `/v1/users/send-email/${hostEmail}`,
        { value: requestedValue ? "YES" : "NO" },
        { headers: apiHeaders },
      );
      return requestedValue;
    });
  };

  const redeem = async () => {
    if (!detailedUser.freeScreening) return;
    const hostEmail = resolveHostEmail();
    if (!hostEmail) return;
    const res = await axios.post(
      "/v1/users/free-to-main",
      { email: hostEmail },
      { headers: apiHeaders },
    );
    if (res.data.status === "success") {
      addToast({
        severity: "success",
        summary: "Success",
        detail: res.data.message,
        life: 10000,
      });
      reload();
    }
  };

  const emailNext = async () => {
    const hostEmail = resolveHostEmail();
    if (!hostEmail) return;
    await axios.post(
      `/v1/users/send-email/${hostEmail}`,
      { value: sendEmail ? "YES" : "NO" },
      { headers: apiHeaders },
    );
    confirmedToggleRef.current[TOGGLE_SEND_EMAIL] = sendEmail;
    desiredToggleRef.current[TOGGLE_SEND_EMAIL] = sendEmail;
    setPopupStep(3);
  };

  const closeModal = async () => {
    const hostEmail = resolveHostEmail();
    if (!hostEmail) return;
    await axios.get(`/v1/users/turn-on-popup-closed/${hostEmail}`, {
      headers: apiHeaders,
    });
    setIsPopupClosed(true);
  };

  if (!user || !detailedUser) return <div className="loading-container" />;

  if (!isPopupClosed) {
    return (
      <div className="popupContainer" role="presentation">
        {popupStep === 1 ? (
          <div className="popup" role="dialog" aria-modal="true" aria-labelledby="account-onboarding-title">
            <div className="popupStepLabel">
              <span>Step 1 of 3</span>
              <strong>Guest Verification Settings</strong>
            </div>
            <div className="popupBody">
              <div className="popupHeader">
                <span className="popupHeroIcon" aria-hidden="true">
                  <i className="pi pi-shield" />
                </span>
                <h3 id="account-onboarding-title">Configure Guest Verification</h3>
              </div>
              <div className="popupVerificationOptions">
                <div className="popupIdBox popupVerificationRow">
                  <span className="popupFeatureIcon" aria-hidden="true">
                    <i className="pi pi-id-card" />
                  </span>
                  <div className="popupVerificationCopy">
                    <h5>ID Verification</h5>
                    <p>When enabled, guests will be required to upload a government-issued photo ID before submitting the form.</p>
                  </div>
                  <button
                    type="button"
                    className={isIdVerificationEnabled ? "option-switch active" : "option-switch"}
                    onClick={() => changeIdVerification(true)}
                    aria-label="Toggle ID Verification"
                    aria-pressed={isIdVerificationEnabled}
                    aria-busy={toggleSavingState[TOGGLE_ID_VERIFICATION]}
                  >
                    <div />
                  </button>
                </div>
                <div className="popupIdBox popupVerificationRow">
                  <span className="popupFeatureIcon selfie" aria-hidden="true">
                    <i className="pi pi-camera" />
                  </span>
                  <div className="popupVerificationCopy">
                    <h5>Selfie Verification</h5>
                    <p>When enabled, guests will be asked to take a selfie, which will be compared with the photo on their ID. ID Verification must be enabled for this setting to work. Each completed screening with Selfie Verification uses 1 additional screening credit (2 credits total).</p>
                  </div>
                  <button
                    type="button"
                    className={isSelfieVerificationEnabled ? "option-switch active" : "option-switch"}
                    onClick={() => changeSelfieVerification(true)}
                    aria-label="Toggle Selfie Verification"
                    aria-pressed={isSelfieVerificationEnabled}
                    aria-busy={toggleSavingState[TOGGLE_SELFIE_VERIFICATION]}
                  >
                    <div />
                  </button>
                </div>
              </div>
              <div className="popupHint">
                <i className="pi pi-info-circle" aria-hidden="true" />
                <p>These choices become your Default settings and are applied to every current listing. New listings inherit the same defaults.</p>
              </div>
            </div>
            <div className="popupBottom">
              <div className="popupSteps">
                <div className="popupStep active" aria-label="Current step" />
                <div className="popupStep" />
                <div className="popupStep" />
              </div>
              <div className="popupButtons">
                <Button
                  label="Next"
                  onClick={() => setPopupStep(2)}
                  disabled={toggleSavingState[TOGGLE_ID_VERIFICATION] || toggleSavingState[TOGGLE_SELFIE_VERIFICATION]}
                />
              </div>
            </div>
          </div>
        ) : null}
        {popupStep === 2 ? (
          <div className="popup second" role="dialog" aria-modal="true" aria-labelledby="account-email-title">
            <div className="popupStepLabel">
              <span>Step 2 of 3</span>
              <strong>Notifications</strong>
            </div>
            <div className="popupBody popupNotificationBody">
              <div className="popupHeader popupNotificationHeader">
                <span className="popupHeroIcon email" aria-hidden="true">
                  <i className="pi pi-envelope" />
                </span>
                <h2 id="account-email-title">Select How You Receive Results</h2>
              </div>
              <div className="popupOptionsList">
                <button
                  type="button"
                  className={sendEmail ? "popupOption active" : "popupOption"}
                  onClick={() => setSendEmail(true)}
                  aria-pressed={sendEmail}
                >
                  <span className="popupOptionCheck" aria-hidden="true">
                    <i className="pi pi-check" />
                  </span>
                  <div>
                    <h4>Email me guest screening results</h4>
                    <p>Receive the report in your inbox as soon as it's ready.</p>
                  </div>
                </button>
                <button
                  type="button"
                  className={!sendEmail ? "popupOption active" : "popupOption"}
                  onClick={() => setSendEmail(false)}
                  aria-pressed={!sendEmail}
                >
                  <span className="popupOptionCheck" aria-hidden="true">
                    <i className="pi pi-minus" />
                  </span>
                  <div>
                    <h4>Don't email me guest screening results</h4>
                    <p>View screening reports in your account instead.</p>
                  </div>
                </button>
              </div>
              <div className="popupHint">
                <i className="pi pi-info-circle" aria-hidden="true" />
                <p>You can adjust these checks later in Account Settings.</p>
              </div>
            </div>
            <div className="popupBottom">
              <div className="popupSteps">
                <div className="popupStep active" />
                <div className="popupStep active" />
                <div className="popupStep" />
              </div>
              <div className="popupButtons">
                <Button label="Next" onClick={emailNext} />
              </div>
            </div>
          </div>
        ) : null}
        {popupStep === 3 ? (
          <div className="popup complete" role="dialog" aria-modal="true" aria-labelledby="account-complete-title">
            <div className="popupStepLabel">
              <span>Step 3 of 3</span>
              <strong>Ready to screen</strong>
            </div>
            <div className="popupBody popupCompleteBody">
              <div className="popupHeader popupCompleteHeader">
                <span className="popupHeroIcon complete" aria-hidden="true">
                  <i className="pi pi-check" />
                </span>
                <span className="popupReadyBadge"><i className="pi pi-star-fill" aria-hidden="true" /> Setup complete</span>
                <h1 id="account-complete-title">Your Verification Setup Is Ready</h1>
                <p>Your preferences have been saved. You can now begin screening guests..</p>
              </div>
              <div className="popupReadySummary" aria-label="Saved verification preferences">
                <div className="popupReadySummaryHead">
                  <div>
                    <span>Saved preferences</span>
                    <strong>Your guest screening setup</strong>
                  </div>
                  <i className="pi pi-shield" aria-hidden="true" />
                </div>
                <div className="popupReadySetting">
                  <span className="popupReadySettingIcon"><i className="pi pi-id-card" aria-hidden="true" /></span>
                  <strong>ID Verification</strong>
                  <span className={isIdVerificationEnabled ? "popupReadyStatus active" : "popupReadyStatus"}>
                    {isIdVerificationEnabled ? "ON" : "OFF"}
                  </span>
                </div>
                <div className="popupReadySetting">
                  <span className="popupReadySettingIcon selfie"><i className="pi pi-camera" aria-hidden="true" /></span>
                  <strong>Selfie Verification</strong>
                  <span className={isSelfieVerificationEnabled ? "popupReadyStatus active" : "popupReadyStatus"}>
                    {isSelfieVerificationEnabled ? "ON" : "OFF"}
                  </span>
                </div>
                <div className="popupReadySetting">
                  <span className="popupReadySettingIcon email"><i className="pi pi-envelope" aria-hidden="true" /></span>
                  <strong>Notifications</strong>
                  <span className={sendEmail ? "popupReadyStatus active" : "popupReadyStatus"}>
                    {sendEmail ? "ON" : "OFF"}
                  </span>
                </div>
              </div>
            </div>
            <div className="popupBottom">
              <div className="popupSteps">
                <div className="popupStep active" />
                <div className="popupStep active" />
                <div className="popupStep active" />
              </div>
              <Button label="Done" onClick={closeModal} />
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  if (currentStage === STATE_ACTIVE_ACCOUNT) {
    return (
      <div className="account-page-modern">
        <div className="cardContainer userAccount userAccount-modern">
          <section className="account-stage-card">
            <h2>Active Account</h2>
            <p>
              Your account will be reactivated. Check your account balance for
              available Guest Screening Credits.
            </p>
            <div className="account-stage-actions">
              <Button
                label="Go Back"
                className="btn-danger"
                onClick={() => setCurrentStage(STATE_ACCOUNT)}
              />
              <Button
                label="Reactivate My Account"
                onClick={async () => {
                  const res = await axios.post(
                    "/v1/users/active",
                    { email: user.username },
                    { headers: apiHeaders },
                  );
                  if (res.data.status === "success") {
                    addToast({
                      severity: "success",
                      summary: "Success",
                      detail: res.data.message,
                      life: 10000,
                    });
                    setDetailedUser((d) => ({ ...d, status: "ACTIVE" }));
                    setCurrentStage(STATE_ACCOUNT);
                  }
                }}
              />
            </div>
          </section>
        </div>
      </div>
    );
  }

  if (currentStage === STATE_HOSTAWAY_ACCOUNT) {
    return (
      <div className="account-page-modern">
        <div className="cardContainer userAccount userAccount-modern">
          <section className="account-stage-card account-form-card">
            <h2>Link Your Account To Hostaway</h2>
            <Field label="Account ID" required>
              <input
                className="form-control"
                placeholder="Account ID"
                value={accountId}
                onChange={(event) => setAccountId(event.target.value)}
              />
            </Field>
            <Field label="API Key" required>
              <input
                className="form-control"
                placeholder="API Key"
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
              />
            </Field>
            <div className="account-stage-actions">
              <Button
                label="Go Back"
                className="btn-danger"
                onClick={() => setCurrentStage(STATE_ACCOUNT)}
              />
              <Button label="Connect" onClick={completeHostaway} />
            </div>
          </section>
        </div>
      </div>
    );
  }

  const canLink =
    !userLinkedIsLoading &&
    !userLinked &&
    ["ownerrez", "hostaway", "hospitable"].includes(detailedUser.channel);
  const canDelink =
    !userLinkedIsLoading &&
    userLinked &&
    ["ownerrez", "hostaway", "hospitable"].includes(detailedUser.channel);
  const channelLabel = formatChannelLabel(detailedUser.channel);
  const pmsConnectionLabel = userLinkedIsLoading
    ? "Checking..."
    : canDelink
      ? `Connected to ${channelLabel}`
      : canLink
        ? "Not connected"
        : "Not configured";

  return (
    <div className="account-page-modern">
      <div className="cardContainer userAccount userAccount-modern">
        <div className="account-page-head">
          <div>
            <p className="dashboard-section-kicker">Settings</p>
            <h2>Account</h2>
          </div>
        </div>
        <div className="account-sections">
          <AccountSection
            title="Account Details"
            actions={
              <>
                {detailedUser.status === "SNOOZED" ? (
                  <Button
                    label="Reactivate Account"
                    onClick={() => setCurrentStage(STATE_ACTIVE_ACCOUNT)}
                  />
                ) : null}
                {detailedUser.status === "ACTIVE" ? (
                  <Button
                    label="Top Up Now"
                    onClick={() => navigate("/topup")}
                  />
                ) : null}
                {canLink ? (
                  <Button
                    label={`Link to ${channelLabel} Account`}
                    onClick={linkAccount}
                  />
                ) : null}
                {canDelink ? (
                  <Button
                    label={`Disconnect ${channelLabel}`}
                    className="btn-danger"
                    onClick={() => setShowDisconnectConfirm(true)}
                  />
                ) : null}
              </>
            }
          >
            <div className="account-fields-grid">
              <AccountValue
                label="Member Since"
                value={memberSince ? formatDate(memberSince) : "N/A"}
              />
              <AccountValue
                label="Account Status"
                value={detailedUser.status}
              />
              <AccountValue
                label="Credits Available"
                value={detailedUser.screening || "0"}
              />
              <AccountValue label="PMS Connection" value={pmsConnectionLabel} />
            </div>
          </AccountSection>

          <AccountSection
            title="Security"
            actions={
              <>
                <Button
                  label="Update Password"
                  onClick={() => navigate("/password")}
                />
                <Button
                  label="Update Email"
                  onClick={() => navigate("/email")}
                />
              </>
            }
          >
            <div className="account-fields-grid">
              <AccountValue label="Email" value={user.username} />
              <AccountValue label="Password" value="••••••••" />
            </div>
          </AccountSection>

          <AccountSection
            title="Host Info"
            actions={
              <Button label="Update" onClick={() => navigate("/userinfo")} />
            }
          >
            <div className="account-fields-grid">
              <AccountValue label="Email" value={user.email} />
              <AccountValue label="Name" value={user.contactName} />
              <AccountValue label="Company" value={user.company} />
              <AccountValue label="Website" value={user.website} />
              <AccountValue label="Country Code" value={user.country} />
              <AccountValue label="Phone" value={user.phone} />
              <AccountValue label="Address" value={user.address} />
            </div>
          </AccountSection>

          <AccountSection title="Configure Screening & Notification Settings">
            <div className="settings-switches">
              <div className="account-listing-management-actions">
                <Button onClick={() => { setManualListingError(""); setShowManualListing(true); }}>
                  <i className="pi pi-plus" aria-hidden="true" /> Add Listings Manually
                </Button>
                <Button className="btn-secondary" loading={listingSettingsRefreshing} loadingLabel="Syncing Listings..." onClick={syncAccountListings}>
                  <i className="pi pi-sync" aria-hidden="true" /> Sync Listings from your PMS
                </Button>
              </div>
              <div className="account-listing-settings">
                <div className="account-listing-settings-content">
                  {listingSettingsError ? <div className="account-listing-error"><i className="pi pi-exclamation-triangle" />Listing refresh failed.</div> : null}
                  <div className="account-listing-table-scroll">
                    <table className="account-listing-table">
                      <thead>
                        <tr><th>Listing</th><th>Background Screening</th><th>ID Verification</th><th>Selfie Verification<sup>*</sup></th><th>Actions</th></tr>
                      </thead>
                      <tbody>
                        <tr className="account-listing-apply-row">
                          <th scope="row">
                            <button
                              type="button"
                              className="account-listing-apply-button"
                              disabled={
                                bulkListingSettingsSaving ||
                                toggleSavingState[TOGGLE_ID_VERIFICATION] ||
                                toggleSavingState[TOGGLE_SELFIE_VERIFICATION]
                              }
                              onClick={applySettingsToAllListings}
                              aria-busy={bulkListingSettingsSaving}
                            >
                              {bulkListingSettingsSaving ? (
                                <i className="pi pi-spin pi-spinner" aria-hidden="true" />
                              ) : (
                                <i className="pi pi-check-square" aria-hidden="true" />
                              )}
                              {bulkListingSettingsSaving ? "Applying..." : "Apply to All Listings"}
                            </button>
                          </th>
                          <td><button type="button" className="mini-setting-switch active" disabled aria-pressed="true" aria-label="Background Screening is always enabled for all listings"><i /></button></td>
                          <td><button type="button" className={`mini-setting-switch ${isIdVerificationEnabled ? "active" : ""}`} disabled={bulkListingSettingsSaving || toggleSavingState[TOGGLE_ID_VERIFICATION]} onClick={() => changeIdVerification()} aria-pressed={isIdVerificationEnabled} aria-label="Default ID Verification"><i /></button></td>
                          <td><button type="button" className={`mini-setting-switch ${isSelfieVerificationEnabled ? "active" : ""}`} disabled={bulkListingSettingsSaving || toggleSavingState[TOGGLE_SELFIE_VERIFICATION]} onClick={() => changeSelfieVerification()} aria-pressed={isSelfieVerificationEnabled} aria-label="Default Selfie Verification"><i /></button></td>
                          <td />
                        </tr>
                        {listings.map((listing) => (
                          <tr key={listing.id}>
                            <th scope="row"><span>{listing.name}{listingSavingIds[listing.id] ? <i className="pi pi-spin pi-spinner account-listing-row-spinner" aria-label={`Saving ${listing.name}`} /> : null}</span><small style={{fontSize: 12}}>{listing.source === "pms" ? `Synced from ${formatChannelLabel(listing.provider)}` : "Manual listing"}</small></th>
                            <td><button type="button" className="mini-setting-switch active" disabled aria-pressed="true" aria-label={`Background Screening is always enabled for ${listing.name}`}><i /></button></td>
                            <td><button type="button" className={`mini-setting-switch ${listing.isIdVerificationEnabled ? "active" : ""}`} disabled={Boolean(listingSavingIds[listing.id])} onClick={() => changeListingRequirement(listing, "isIdVerificationEnabled")} aria-pressed={Boolean(listing.isIdVerificationEnabled)} aria-label={`ID Verification for ${listing.name}`}><i /></button></td>
                            <td><button type="button" className={`mini-setting-switch ${listing.isSelfieVerificationEnabled ? "active" : ""}`} disabled={Boolean(listingSavingIds[listing.id])} onClick={() => changeListingRequirement(listing, "isSelfieVerificationEnabled")} aria-pressed={Boolean(listing.isSelfieVerificationEnabled)} aria-label={`Selfie Verification for ${listing.name}`}><i /></button></td>
                            <td>
                              {listing.source === "manual" ? (
                                <Button
                                  label="Delete"
                                  loadingLabel="Deleting..."
                                  className="btn-danger account-listing-delete-button"
                                  disabled={Boolean(listingSavingIds[listing.id])}
                                  onClick={() => deleteManualListing(listing)}
                                />
                              ) : null}
                            </td>
                          </tr>
                        ))}
                        {listings.length === 0 ? <tr className="account-listing-empty-row"><td colSpan="5">No listings have been added yet.</td></tr> : null}
                      </tbody>
                    </table>
                  </div>
                  <div className="account-listing-credit-note"><span>* Credit Usage: Selfie Verification uses 1 additional screening credit and requires ID Verification. Standard Screening with Selfie Verification uses 2 credits in total.</span></div>
                </div>
              </div>
              <div className="switch-row">
                <div
                  className={
                    sendEmail ? "option-switch active" : "option-switch"
                  }
                  onClick={changeSendEmail}
                  aria-busy={toggleSavingState[TOGGLE_SEND_EMAIL]}
                >
                  <div />
                </div>
                <p>
                  Email Notification{" "}
                  <span>
                    {sendEmail ? "ON" : "OFF"}
                    {toggleSavingState[TOGGLE_SEND_EMAIL] ? " (Saving...)" : ""}
                  </span>
                </p>
              </div>
            </div>
          </AccountSection>
        </div>
        <Modal
          title="Add Listing Manually"
          description="Add a property that is not managed by your PMS."
          icon="pi-home"
          visible={showManualListing}
          width="min(92vw, 560px)"
          onClose={manualListingSaving ? undefined : () => setShowManualListing(false)}
          footer={
            <div className="account-confirm-actions">
              <Button label="Cancel" className="btn-secondary" disabled={manualListingSaving} onClick={() => setShowManualListing(false)} />
              <Button label="Add Listing" type="submit" form="manual-listing-form" loading={manualListingSaving} />
            </div>
          }
        >
          <form id="manual-listing-form" className="account-manual-listing-form" onSubmit={saveManualListing}>
            <Field label="Listing Name" required error={manualListingError}>
              <input className="form-control" value={manualListing.name} autoFocus placeholder="e.g. Ocean View Apartment" onChange={(event) => { setManualListing((current) => ({ ...current, name: event.target.value })); setManualListingError(""); }} />
            </Field>
            <Field label="Address">
              <input className="form-control" value={manualListing.address} placeholder="Optional address" onChange={(event) => setManualListing((current) => ({ ...current, address: event.target.value }))} />
            </Field>
            <div className="account-manual-listing-defaults">
              <i className="pi pi-shield" aria-hidden="true" />
              <span><strong>Screening selections</strong><small>Background Screening On · ID {isIdVerificationEnabled ? "On" : "Off"} · Selfie {isSelfieVerificationEnabled ? "On" : "Off"}</small></span>
            </div>
          </form>
        </Modal>
        <Modal
          title="Disconnect PMS Account"
          icon="pi-exclamation-triangle"
          tone="danger"
          visible={showDisconnectConfirm}
          width="min(92vw, 460px)"
          onClose={
            isDisconnectingPms
              ? undefined
              : () => setShowDisconnectConfirm(false)
          }
          footer={
            <div className="account-confirm-actions">
              <Button
                label="Cancel"
                className="btn-secondary"
                disabled={isDisconnectingPms}
                onClick={() => setShowDisconnectConfirm(false)}
              />
              <Button
                label={
                  isDisconnectingPms ? "Disconnecting..." : "Yes, Disconnect"
                }
                className="btn-danger"
                disabled={isDisconnectingPms}
                onClick={delinkAccount}
              />
            </div>
          }
        >
          <div className="account-confirm-modal">
            <div className="account-confirm-icon">
              <i className="pi pi-exclamation-triangle" aria-hidden="true" />
            </div>
            <h4>Are you sure?</h4>
            <p>
              Disconnecting {channelLabel || "your PMS"} will stop Trustd.ai
              from syncing PMS data for this account. You can reconnect it later
              from this page.
            </p>
          </div>
        </Modal>
      </div>
    </div>
  );
}
